import { BoxStaffCoachIcon } from "@images/svgIcons/boxStaff/boxStaffCoachIcon"
import { BoxStaffCoOwnerIcon } from "@images/svgIcons/boxStaff/boxStaffCoOwnerIcon"
import { BoxStaffHeadCoachIcon } from "@images/svgIcons/boxStaff/boxStaffHeadCoachIcon"
import { ForbiddenIcon } from "@images/svgIcons/forbiddenIcon"
import { BoxStaffDetailedEntity } from "@modules/boxStaff/entities/boxStaffEntity"
import { BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaff } from "@modules/boxStaff/hooks/useBoxStaff"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HUDialog } from "@modules/ui/components/popup/huDialog"
import { useCallback, useImperativeHandle, useState } from "react"
import { useTheme } from "styled-components"

export type BoxStaffRoleModalRef = {
  open: () => void
}

export type BoxStaffRoleModalProps = {
  staffDetailed: BoxStaffDetailedEntity
  ref: React.Ref<BoxStaffRoleModalRef>
}

export function BoxStaffRoleModal({ staffDetailed, ref }: BoxStaffRoleModalProps) {
  const [showModal, setShowModal] = useState(false)
  const [role, setRole] = useState<BoxStaffRole | undefined>(staffDetailed.role)

  const theme = useTheme()
  const toast = useToast()
  const { hideSidebar } = useSidebar()
  const { updateBoxStaff, deleteBoxStaff, isPending } = useBoxStaff(staffDetailed.id)

  const changeRole = (role?: BoxStaffRole): void => {
    setRole(role)
  }

  const onHide = useCallback(() => {
    setShowModal(false)
  }, [])

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
    },
  }))

  async function handleOnSubmit(): Promise<void> {
    if (role) {
      await updateBoxStaff({ role })
        .then(() => {
          toast.show({
            severity: "success",
            detail: word("box.staff.roleModal.toast.update.success"),
          })
        })
        .catch(() => {
          toast.show({
            severity: "error",
            detail: word("box.staff.roleModal.toast.update.error"),
          })
        })
    } else if (role === undefined) {
      await deleteBoxStaff()
        .then(() => {
          toast.show({
            severity: "success",
            detail: word("box.staff.roleModal.toast.delete.success"),
          })
          hideSidebar()
        })
        .catch(() => {
          toast.show({
            severity: "error",
            detail: word("box.staff.roleModal.toast.delete.error"),
          })
        })
    }
    onHide()
  }

  return (
    <HUDialog visible={showModal} header={""} onHide={onHide} style={{ width: "480px" }}>
      {/* HEADER */}
      <div className="flex flex-column flex-wrap gap-2 justify-content-center align-content-center text-center ">
        <HUText fontStyle="TL" color={theme.mainWhite}>
          {word("box.staff.roleModal.label.header")}
        </HUText>
        <HUText fontStyle="BM" color={theme.neutralColor600}>
          {word("box.staff.roleModal.label.subHeader")}
        </HUText>
      </div>
      {/* ROLE */}
      <div className="flex flex-column mt-4 gap-2">
        <div
          className={`flex align-items-center gap-2 px-3 py-2 border-1 border-round-lg  ${
            role === BoxStaffRole.COACH
              ? "border-electricPurple-400"
              : "cursor-pointer border-neutralColor-700 hover:surface-hover"
          }`}
          onClick={() => changeRole(BoxStaffRole.COACH)}
        >
          <BoxStaffCoachIcon color={theme.mainWhite} size={50} />
          <HUText fontStyle="LL" color={theme.mainWhite}>
            {word("box.staff.roleModal.global.coach")}
          </HUText>
        </div>
        <div
          className={`flex align-items-center gap-2 px-3 py-2 border-1 border-round-lg  ${
            role === BoxStaffRole.HEAD_COACH
              ? "border-electricPurple-400"
              : "cursor-pointer border-neutralColor-700 hover:surface-hover"
          }`}
          onClick={() => changeRole(BoxStaffRole.HEAD_COACH)}
        >
          <BoxStaffHeadCoachIcon color={theme.mainWhite} size={50} />
          <HUText fontStyle="LL" color={theme.mainWhite}>
            {word("box.staff.roleModal.global.headCoach")}
          </HUText>
        </div>
        <div
          className={`flex align-items-center gap-2 px-3 py-2 border-1 border-round-lg  ${
            role === BoxStaffRole.CO_OWNER
              ? "border-electricPurple-400"
              : "cursor-pointer border-neutralColor-700 hover:surface-hover"
          }`}
          onClick={() => changeRole(BoxStaffRole.CO_OWNER)}
        >
          <BoxStaffCoOwnerIcon color={theme.mainWhite} size={50} />
          <HUText fontStyle="LL" color={theme.mainWhite}>
            {word("box.staff.roleModal.global.coOwner")}
          </HUText>
        </div>
        {/* <div
          className={`flex align-items-center gap-2 px-3 py-2 border-1 border-round-lg ${
            role === BoxStaffRole.FINANCIAL
              ? "border-electricPurple-400"
              : " cursor-pointer border-neutralColor-700 hover:surface-hover"
          }`}
          onClick={() => changeRole(BoxStaffRole.FINANCIAL)}
        >
          <BoxStaffFinancialIcon color={theme.mainWhite} size={50} />
          <HUText fontStyle="LL" color={theme.mainWhite}>
            Financial
          </HUText>
        </div> */}
        <div
          className={`flex align-items-center gap-2 px-3 py-2 border-1 border-round-lg ${
            role === undefined
              ? "border-electricPurple-400"
              : "cursor-pointer border-neutralColor-700 hover:surface-hover"
          }`}
          onClick={() => changeRole()}
        >
          <ForbiddenIcon color={theme.primaryColor} width={50} height={50} />
          <div className="flex flex-column">
            <HUText fontStyle="LL" color={theme.mainWhite}>
              {word("box.staff.roleModal.global.noRole")}
            </HUText>
            <HUText fontStyle="BS" color={theme.mainWhite}>
              ({word("box.staff.roleModal.global.noRoleSub")})
            </HUText>
          </div>
        </div>
      </div>
      {/* BUTTON */}
      <div className=" mb-4 mt-4 flex flex-row gap-2 align-items-center justify-content-center">
        <HUButton
          type="Left_Icon"
          size="M"
          colorType="Quaternary"
          text={word("global.cancel")}
          onClick={() => onHide()}
          className="w-full"
        />
        <HUButton
          type="Right_Icon"
          size="M"
          colorType="Primary"
          text={word("global.check")}
          onClick={() => staffDetailed.role !== role && handleOnSubmit()}
          disabled={staffDetailed.role === role || isPending}
          className="w-full"
        />
      </div>
    </HUDialog>
  )
}
